// src/containers/pages/caseManagement/CaseManagementDashboard.tsx

import React, { useState } from 'react';
import { Box, Typography } from 'components/mui';
import LoadingBackdrop from 'components/LoadingBackdrop';
import useCaseManagement from './hooks/useCaseManagement';
import CustomerSelector from './components/CustomerSelector';
import CasePanels from './components/CasePanels';
import CommentTable from './components/CommentTable';
import TableToolbar from './components/TableToolbar';
import MoveConfirmDialog from './components/MoveConfirmDialog';
import DeleteCaseButton from './components/DeleteCaseButton';
import DeleteCaseConfirmDialog from './components/DeleteCaseConfirmDialog';

function CaseManagementDashboard() {
  const { state, handler, derived } = useCaseManagement();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleMoveClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirmMove = () => {
    setConfirmOpen(false);
    handler.onMoveComments();
  };

  const handleCancelMove = () => {
    setConfirmOpen(false);
  };

  const handleDeleteConfirm = async () => {
    const confirmedAttachments =
      state.deletability?.state === 'WARN_ATTACHMENTS';
    const deleted = await handler.onDeleteCase(confirmedAttachments);
    if (deleted) setDeleteDialogOpen(false);
    // on failure the dialog stays open; the hook re-fetched deletability and
    // toasted the server's reason
  };

  return (
    <Box
      component="main"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* Page title */}
      <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
        Case Management
      </Typography>

      {/* Customer selector */}
      <CustomerSelector
        customer={state.customer}
        onSelect={handler.onSelectCustomer}
        disabled={state.applying}
      />

      {/* Source + Destination case panels (side-by-side) */}
      {state.customer && (
        <CasePanels
          customer={state.customer}
          sourceCase={state.sourceCase}
          destinationCase={state.destinationCase}
          onSelectSourceCase={handler.onSelectSourceCase}
          onSelectDestinationCase={handler.onSelectDestinationCase}
          disabled={state.applying}
          sourceResetSignal={state.sourceResetSignal}
          sourceHeaderAction={
            <DeleteCaseButton
              buttonState={derived.deleteButtonState}
              blockedTooltip={derived.deleteBlockedTooltip}
              moveInProgress={state.applying}
              deleting={state.deleting}
              onClick={() => setDeleteDialogOpen(true)}
            />
          }
        />
      )}

      {/* Table toolbar: employee filter + counts + MOVE button */}
      {state.sourceCase && (
        <TableToolbar
          employees={derived.employees}
          selectedEmployeeId={state.employeeFilter}
          onEmployeeFilter={handler.onEmployeeFilter}
          totalComments={state.comments.length}
          filteredCount={state.filteredComments.length}
          selectedCount={state.selectedIds.size}
          canMove={derived.canMove}
          unmappedSelectedCount={derived.unmappedSelectedCount}
          applying={state.applying}
          progress={state.progress}
          failedCount={state.failedCommentIds.length}
          hasDestinationCase={state.destinationCase !== null}
          onMove={handleMoveClick}
          onRetryFailed={handler.onRetryFailed}
        />
      )}

      {/* Comment table with checkboxes + dest activity dropdowns */}
      {state.sourceCase && (
        <CommentTable
          comments={state.filteredComments}
          selectedIds={state.selectedIds}
          onToggleSelect={handler.onToggleSelect}
          onToggleSelectAll={handler.onToggleSelectAll}
          allVisibleSelected={derived.allVisibleSelected}
          someVisibleSelected={derived.someVisibleSelected}
          destinationActivities={state.destinationActivities}
          commentActivityMap={state.commentActivityMap}
          onSetCommentActivity={handler.onSetCommentActivity}
          hasDestinationCase={state.destinationCase !== null}
          applying={state.applying}
          employeeFilterName={
            state.employeeFilter !== null
              ? derived.employees.find((e) => e.id === state.employeeFilter)?.name ?? ''
              : ''
          }
        />
      )}

      {/* Confirmation dialog */}
      <MoveConfirmDialog
        open={confirmOpen}
        selectedCount={state.selectedIds.size}
        sourceCaseTitle={state.sourceCase?.title ?? ''}
        destCaseTitle={state.destinationCase?.title ?? ''}
        onConfirm={handleConfirmMove}
        onCancel={handleCancelMove}
      />

      {/* Guarded case-delete confirmation (red / orange variants) */}
      <DeleteCaseConfirmDialog
        open={deleteDialogOpen}
        deletability={state.deletability}
        deleting={state.deleting}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      {/* Loading backdrop */}
      <LoadingBackdrop
        open={state.loadingComments || state.loadingDestActivities}
      />
    </Box>
  );
}

export default CaseManagementDashboard;
